import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const COUNT = 7000;

function Galaxy({ reduced }) {
  const ref = useRef();

  const { positions, colors } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const innerR = 1.15;
    const outerR = 2.35;
    const purple = new THREE.Color("#a855f7");
    const white = new THREE.Color("#ffffff");
    const dim = new THREE.Color("#6b6b78");

    for (let i = 0; i < COUNT; i++) {
      const t = Math.pow(Math.random(), 0.7);
      const radius = innerR + t * (outerR - innerR);
      const angle = Math.random() * Math.PI * 2;
      // thickness of the ring falls off toward outer edge
      const thickness = 0.35 * (1 - (radius - innerR) / (outerR - innerR)) + 0.06;
      const spread = (Math.random() - 0.5) * thickness;

      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      const z = spread + (Math.random() - 0.5) * 0.12;

      const i3 = i * 3;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;

      // color: mostly cool white, a few purple near inner edge, some dim
      const r = Math.random();
      let c;
      if (r < 0.14) c = purple;
      else if (r < 0.45) c = dim;
      else c = white;
      colors[i3] = c.r;
      colors[i3 + 1] = c.g;
      colors[i3 + 2] = c.b;
    }
    return { positions, colors };
  }, []);

  useFrame((state, delta) => {
    if (!ref.current) return;
    const spin = reduced ? 0 : delta * 0.045;
    ref.current.rotation.z += spin;
    // subtle pointer parallax
    const px = state.pointer.x * 0.18;
    const py = state.pointer.y * 0.14;
    ref.current.rotation.x = THREE.MathUtils.lerp(ref.current.rotation.x, -0.62 + py, 0.05);
    ref.current.rotation.y = THREE.MathUtils.lerp(ref.current.rotation.y, px, 0.05);
    ref.current.position.x = THREE.MathUtils.lerp(ref.current.position.x, px * 0.6, 0.05);
  });

  return (
    <points ref={ref} rotation={[-0.62, 0, 0]}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={COUNT}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={COUNT}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.016}
        vertexColors
        sizeAttenuation
        transparent
        opacity={0.95}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

export default function ParticleField() {
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div className="absolute inset-0" data-testid="particle-field">
      <Canvas
        camera={{ position: [0, 0, 4.1], fov: 50 }}
        dpr={[1, 1.8]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: "transparent" }}
      >
        <Galaxy reduced={reduced} />
      </Canvas>
    </div>
  );
}
